// src/app/modules/Recrutement/onboarding/ParcoursManagement.jsx

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Plus, 
    Search, 
    Edit3, 
    Trash2, 
    Map, 
    Info, 
    ChevronRight,
    Layout,
    Layers,
    Save,
    X,
    Filter,
    Briefcase,
    Users,
    Eye
} from 'lucide-react';
import { 
    fetchParcoursRequest,
    createParcoursRequest, 
    updateParcoursRequest, 
    deleteParcoursRequest,
    assignParcoursToPostesRequest,
    assignParcoursToUtilisateursRequest
} from '../../../redux/actions/parcoursActions';
import { fetchModulesCleRequest } from '../../../redux/actions/modulesCleActions';
import { getAllJobsRequest } from '../../../redux/actions/Jobactions';
import { getAllUsersRequest } from '../../../redux/actions/userActions';
import './ParcoursManagement.css';
import Swal from 'sweetalert2';

const ParcoursManagement = () => {
    const dispatch = useDispatch();
    const { list: parcoursList, loading } = useSelector((state) => state.parcours);
    const { modulesCle } = useSelector((state) => state.modulesCle);
    const { jobs } = useSelector((state) => state.jobs);
    const { users } = useSelector((state) => state.user);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showPostesModal, setShowPostesModal] = useState(false);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [showModulesModal, setShowModulesModal] = useState(false);
    const [selectedParcours, setSelectedParcours] = useState(null);
    const [tempSelectedIds, setTempSelectedIds] = useState([]);
    const [postesSearch, setPostesSearch] = useState('');
    const [usersSearch, setUsersSearch] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        nom: '',
        description: '',
        moduleIds: []
    });

    useEffect(() => {
        dispatch(fetchParcoursRequest());
        dispatch(fetchModulesCleRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    const handleOpenModal = (parcours = null) => {
        if (parcours) {
            setIsEditing(true);
            setFormData({
                id: parcours.id,
                nom: parcours.nom,
                description: parcours.description,
                moduleIds: parcours.modules ? parcours.modules.map(m => m.id) : []
            });
        } else {
            setIsEditing(false);
            setFormData({ id: null, nom: '', description: '', moduleIds: [] });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ id: null, nom: '', description: '', moduleIds: [] });
    };

    const handleToggleModule = (moduleId) => {
        setFormData(prev => {
            const currentIds = prev.moduleIds || [];
            if (currentIds.includes(moduleId)) {
                return { ...prev, moduleIds: currentIds.filter(id => id !== moduleId) };
            } else {
                return { ...prev, moduleIds: [...currentIds, moduleId] };
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleOpenPostesModal = (parcours) => {
        setSelectedParcours(parcours);
        setTempSelectedIds(parcours.assignedPosteIds || []);
        setPostesSearch('');
        setShowPostesModal(true);
    };

    const handleOpenUsersModal = (parcours) => {
        setSelectedParcours(parcours);
        setTempSelectedIds(parcours.assignedUtilisateurIds || []);
        setUsersSearch('');
        setShowUsersModal(true);
    };

    const handleOpenModulesModal = (parcours) => {
        setSelectedParcours(parcours);
        setShowModulesModal(true);
    };

    const handleToggleId = (id) => {
        if (tempSelectedIds.includes(id)) {
            setTempSelectedIds(tempSelectedIds.filter(x => x !== id));
        } else {
            setTempSelectedIds([...tempSelectedIds, id]);
        }
    };

    const handleSaveAssignments = (type) => {
        if (type === 'postes') {
            dispatch(assignParcoursToPostesRequest(selectedParcours.id, tempSelectedIds));
            setShowPostesModal(false);
        } else {
            dispatch(assignParcoursToUtilisateursRequest(selectedParcours.id, tempSelectedIds));
            setShowUsersModal(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.nom || !formData.moduleIds || formData.moduleIds.length === 0) {
            Swal.fire('Attention', 'Veuillez remplir les champs obligatoires.', 'warning');
            return;
        }

        const dataToSend = {
            nom: formData.nom,
            description: formData.description,
            moduleIds: formData.moduleIds
        };

        if (isEditing) {
            dispatch(updateParcoursRequest(formData.id, dataToSend));
        } else {
            dispatch(createParcoursRequest(dataToSend));
        }
        handleCloseModal();
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Cette action est irréversible !",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Oui, supprimer !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteParcoursRequest(id));
            }
        });
    };

    const filteredParcours = parcoursList.filter(p => 
        p.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="pm-page fade-in">
            <div className="pm-main-tabs">
                <button className="pm-main-tab active" type="button">
                    Gestion des Parcours
                </button>
            </div>

            <div className="pm-content">
                {/* Header */}
                <div className="pm-header">
                    <div>
                        <h2>
                            <Map size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            Gestion des parcours
                        </h2>
                        <p className="pm-subtitle">Configurez et personnalisez les parcours d'onboarding par module</p>
                    </div>
                    <div className="pm-header-actions">
                        <button className="pm-btn pm-btn-primary" onClick={() => handleOpenModal()} type="button">
                            + Nouveau Parcours
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="pm-toolbar">
                    <input
                        className="pm-search"
                        placeholder="Rechercher un parcours..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <div className="pm-meta">
                        {loading ? 'Chargement...' : `${filteredParcours.length} parcours`}
                    </div>
                </div>

                {/* Content */}
                {loading && parcoursList.length === 0 ? (
                    <div className="pm-loading">
                        <div className="pm-spinner" />
                        <span>Chargement...</span>
                    </div>
                ) : filteredParcours.length === 0 ? (
                    <div className="pm-empty">
                        <Info size={48} style={{ opacity: 0.2, marginBottom: 16 }} />
                        <p>Aucun parcours trouvé</p>
                    </div>
                ) : (
                    <div className="pm-grid">
                        {filteredParcours.map((p) => (
                            <div key={p.id} className="pm-card">
                                <div className="pm-card-top">
                                    <span className="pm-code">
                                        {p.modules?.length || 0} Modules
                                    </span>
                                    <div className="pm-card-actions">
                                        <button className="pm-link-btn" onClick={() => handleOpenModulesModal(p)} title="Voir les modules" type="button">
                                            <Eye size={13} />
                                        </button>
                                        <button className="pm-link-btn" onClick={() => handleOpenModal(p)} title="Modifier" type="button">
                                            <Edit3 size={13} />
                                        </button>
                                        <button className="pm-link-btn danger" onClick={() => handleDelete(p.id)} title="Supprimer" type="button">
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                                <div className="pm-card-name">{p.nom}</div>
                                {p.description && <div className="pm-card-desc">{p.description}</div>}
                                
                                <div className="pm-card-assign-btns">
                                    <button className="pm-assign-btn" onClick={() => handleOpenPostesModal(p)}>
                                        <Briefcase size={12} />
                                        <span>{p.assignedPosteIds?.length || 0} Postes</span>
                                    </button>
                                    <button className="pm-assign-btn" onClick={() => handleOpenUsersModal(p)}>
                                        <Users size={12} />
                                        <span>{p.assignedUtilisateurIds?.length || 0} Users</span>
                                    </button>
                                </div>

                                <div className="pm-card-footer">
                                    <div className="pm-footer-id">ID: {p.id}</div>
                                    <ChevronRight size={15} style={{ opacity: 0.3 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="pm-overlay" onClick={handleCloseModal}>
                    <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>{isEditing ? 'Modifier le parcours' : 'Ajouter un parcours'}</h3>
                            <button className="pm-modal-close" onClick={handleCloseModal} type="button">✕</button>
                        </div>
                        <form className="pm-form" onSubmit={handleSubmit}>
                            <div className="pm-section-title">Informations Générales</div>
                            <div className="pm-modal-grid">
                                <div className="pm-field pm-full">
                                    <label>Nom du parcours *</label>
                                    <input 
                                        name="nom"
                                        value={formData.nom}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Ex: Parcours Intégration Junior"
                                    />
                                </div>
                                <div className="pm-field pm-full">
                                    <label>Modules associés *</label>
                                    <div className="pm-multi-select-list">
                                        {modulesCle.map(m => (
                                            <div 
                                                key={m.id} 
                                                className={`pm-multi-item ${formData.moduleIds?.includes(m.id) ? 'selected' : ''}`}
                                                onClick={() => handleToggleModule(m.id)}
                                            >
                                                <div className="pm-item-check">
                                                    {formData.moduleIds?.includes(m.id) ? '✓' : ''}
                                                </div>
                                                <div className="pm-item-label">{m.nom}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <span className="pm-field-hint">{formData.moduleIds?.length || 0} module(s) sélectionné(s)</span>
                                </div>
                                <div className="pm-field pm-full">
                                    <label>Description</label>
                                    <textarea 
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={3}
                                        placeholder="Détails du parcours..."
                                    />
                                </div>
                            </div>
                            <div className="pm-modal-actions">
                                <button type="button" className="pm-btn pm-btn-outline" onClick={handleCloseModal}>Annuler</button>
                                <button type="submit" className="pm-btn pm-btn-primary">
                                    <Save size={14} />
                                    {isEditing ? 'Enregistrer' : 'Créer le parcours'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Affectation Postes */}
            {showPostesModal && (
                <div className="pm-overlay" onClick={() => setShowPostesModal(false)}>
                    <div className="pm-modal pm-modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>Affecter des postes au parcours</h3>
                            <button className="pm-modal-close" onClick={() => setShowPostesModal(false)}>✕</button>
                        </div>
                        <div className="pm-modal-body">
                            <p className="pm-modal-desc">Sélectionnez les postes qui doivent suivre ce parcours d'intégration.</p>
                            
                            <div className="pm-modal-search">
                                <Search size={16} />
                                <input 
                                    type="text" 
                                    placeholder="Rechercher par nom ou code..." 
                                    value={postesSearch}
                                    onChange={(e) => setPostesSearch(e.target.value)}
                                />
                            </div>

                            <div className="pm-selection-list">
                                {jobs.filter(job => 
                                    job.name?.toLowerCase().includes(postesSearch.toLowerCase()) || 
                                    job.code?.toLowerCase().includes(postesSearch.toLowerCase())
                                ).map(job => (
                                    <div 
                                        key={job.id} 
                                        className={`pm-selection-item ${tempSelectedIds.includes(job.id) ? 'selected' : ''}`}
                                        onClick={() => handleToggleId(job.id)}
                                    >
                                        <div className="pm-item-info">
                                            <div className="pm-item-title">{job.name}</div>
                                            <div className="pm-item-subtitle">{job.code} — {job.category}</div>
                                        </div>
                                        <div className="pm-item-checkbox">
                                            {tempSelectedIds.includes(job.id) ? '✓' : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="pm-modal-actions">
                            <button className="pm-btn pm-btn-outline" onClick={() => setShowPostesModal(false)}>Annuler</button>
                            <button className="pm-btn pm-btn-primary" onClick={() => handleSaveAssignments('postes')}>
                                <Save size={14} />
                                Enregistrer les affectations
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Affectation Utilisateurs */}
            {showUsersModal && (
                <div className="pm-overlay" onClick={() => setShowUsersModal(false)}>
                    <div className="pm-modal pm-modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>Affecter des utilisateurs au parcours</h3>
                            <button className="pm-modal-close" onClick={() => setShowUsersModal(false)}>✕</button>
                        </div>
                        <div className="pm-modal-body">
                            <p className="pm-modal-desc">Affectation directe à des collaborateurs spécifiques.</p>
                            
                            <div className="pm-modal-search">
                                <Search size={16} />
                                <input 
                                    type="text" 
                                    placeholder="Rechercher par nom, email ou matricule..." 
                                    value={usersSearch}
                                    onChange={(e) => setUsersSearch(e.target.value)}
                                />
                            </div>

                            <div className="pm-selection-list">
                                {Array.isArray(users) && users.filter(user => {
                                    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
                                    const email = (user.email || '').toLowerCase();
                                    const code = (user.employeeCode || '').toLowerCase();
                                    const search = usersSearch.toLowerCase();
                                    return fullName.includes(search) || email.includes(search) || code.includes(search);
                                }).map(user => (
                                    <div 
                                        key={user.id} 
                                        className={`pm-selection-item ${tempSelectedIds.includes(user.id) ? 'selected' : ''}`}
                                        onClick={() => handleToggleId(user.id)}
                                    >
                                        <div className="pm-item-info">
                                            <div className="pm-item-title">{user.firstName} {user.lastName}</div>
                                            <div className="pm-item-subtitle">{user.email} — {user.employeeCode}</div>
                                        </div>
                                        <div className="pm-item-checkbox">
                                            {tempSelectedIds.includes(user.id) ? '✓' : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="pm-modal-actions">
                            <button className="pm-btn pm-btn-outline" onClick={() => setShowUsersModal(false)}>Annuler</button>
                            <button className="pm-btn pm-btn-primary" onClick={() => handleSaveAssignments('users')}>
                                <Save size={14} />
                                Enregistrer les affectations
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal Voir Modules */}
            {showModulesModal && selectedParcours && (
                <div className="pm-overlay" onClick={() => setShowModulesModal(false)}>
                    <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>Modules du parcours : {selectedParcours.nom}</h3>
                            <button className="pm-modal-close" onClick={() => setShowModulesModal(false)}>✕</button>
                        </div>
                        <div className="pm-modal-body">
                            <div className="pm-modules-view-list">
                                {selectedParcours.modules && selectedParcours.modules.length > 0 ? (
                                    selectedParcours.modules.map((m, idx) => (
                                        <div key={m.id} className="pm-module-view-item">
                                            <div className="pm-module-number">{idx + 1}</div>
                                            <div className="pm-module-details">
                                                <div className="pm-module-name">{m.nom}</div>
                                                <div className="pm-module-type">{m.type || 'Module Standard'}</div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="pm-empty-text">Aucun module associé à ce parcours.</p>
                                )}
                            </div>
                        </div>
                        <div className="pm-modal-actions">
                            <button className="pm-btn pm-btn-primary" onClick={() => setShowModulesModal(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParcoursManagement;
